<template>
  <div class="cb">
    <!-- Toggle de modo -->
    <div class="cb-mode">
      <button
        type="button"
        class="cb-mode__btn"
        :class="{ 'cb-mode__btn--active': isVisualMode }"
        @click="isVisualMode = true">
        <fa-icon icon="filter" size="sm" /> Visual
      </button>
      <button
        type="button"
        class="cb-mode__btn"
        :class="{ 'cb-mode__btn--active': !isVisualMode }"
        @click="isVisualMode = false">
        <fa-icon icon="code" size="sm" /> SQL
      </button>
    </div>

    <!-- Modo Visual -->
    <div v-if="isVisualMode" class="cb-visual">
      <div v-if="!entidade" class="cb-empty">
        <fa-icon icon="info-circle" class="cb-empty__icon" />
        <span>Selecione o tipo de tag acima para configurar as condições.</span>
      </div>

      <div v-else-if="loadingFields" class="cb-loading">
        <div class="spinner-border spinner-border-sm" role="status"></div>
        <span>Carregando campos...</span>
      </div>

      <div v-else>
        <!-- Grupos -->
        <div
          v-for="(grupo, gIndex) in conditions.grupos"
          :key="gIndex"
          class="cb-group">

          <!-- Conector AND entre grupos -->
          <div v-if="gIndex > 0" class="cb-connector">
            <span class="cb-connector__line"></span>
            <span class="cb-connector__label">E</span>
            <span class="cb-connector__line"></span>
          </div>

          <div class="cb-group__card">
            <!-- Header do grupo -->
            <div class="cb-group__header">
              <div class="cb-group__title">
                <span class="cb-group__number">{{ gIndex + 1 }}</span>
                <span>Atende</span>
                <select
                  v-model="grupo.logica"
                  class="cb-group__logic"
                  @change="emitUpdate">
                  <option value="AND">todas</option>
                  <option value="OR">qualquer uma</option>
                </select>
                <span>das condições</span>
              </div>
              <button
                type="button"
                class="cb-btn-icon cb-btn-icon--danger"
                @click="removeGroup(gIndex)"
                :disabled="conditions.grupos.length <= 1"
                title="Remover grupo">
                <fa-icon icon="trash-alt" size="sm" />
              </button>
            </div>

            <!-- Condições -->
            <div class="cb-conditions">
              <div
                v-for="(cond, cIndex) in grupo.condicoes"
                :key="cIndex"
                class="cb-row">

                <!-- Prefixo -->
                <div class="cb-row__prefix">
                  <span v-if="cIndex === 0" class="cb-row__prefix-text">Se</span>
                  <span v-else class="cb-row__prefix-text cb-row__prefix-text--logic">
                    {{ grupo.logica === 'AND' ? 'E' : 'OU' }}
                  </span>
                </div>

                <!-- Campo -->
                <div class="cb-row__field">
                  <select
                    v-model="cond.campo"
                    class="cb-select"
                    @change="onFieldChange(cond)">
                    <option value="">Campo...</option>
                    <option
                      v-for="field in fields"
                      :key="field.campo"
                      :value="field.campo">
                      {{ field.label }}
                    </option>
                  </select>
                </div>

                <!-- Operador -->
                <div class="cb-row__operator">
                  <select
                    v-model="cond.operador"
                    class="cb-select"
                    @change="onOperatorChange(cond)"
                    :disabled="!cond.campo">
                    <option value="">Operador...</option>
                    <option
                      v-for="op in getOperatorsForField(cond.campo)"
                      :key="op.value"
                      :value="op.value">
                      {{ op.label }}
                    </option>
                  </select>
                </div>

                <!-- Valor -->
                <div class="cb-row__value" v-if="cond.operador && !isNoValueOperator(cond.operador)">
                  <div class="cb-value-group">
                    <!-- Tipo valor -->
                    <select
                      v-model="cond.tipo_valor"
                      class="cb-select cb-select--tipo"
                      @change="onTipoValorChange(cond)"
                      title="Tipo de valor">
                      <option value="static">Fixo</option>
                      <option value="parametro">Param.</option>
                      <option v-if="getFieldDef(cond.campo).tipo === 'date'" value="named_param">Relativo</option>
                    </select>

                    <!-- Valor fixo -->
                    <template v-if="cond.tipo_valor === 'static'">
                      <select
                        v-if="getFieldDef(cond.campo).tipo === 'boolean'"
                        v-model="cond.valor"
                        class="cb-select cb-select--value"
                        @change="emitUpdate">
                        <option :value="1">Sim</option>
                        <option :value="0">Nao</option>
                      </select>

                      <input
                        v-else-if="getFieldDef(cond.campo).tipo === 'number' && cond.operador !== 'entre'"
                        type="number"
                        v-model.number="cond.valor"
                        class="cb-input"
                        placeholder="Valor"
                        @input="emitUpdate" />

                      <div v-else-if="getFieldDef(cond.campo).tipo === 'number' && cond.operador === 'entre'" class="cb-between">
                        <input
                          type="number"
                          v-model.number="cond.valor[0]"
                          class="cb-input"
                          placeholder="De"
                          @input="emitUpdate" />
                        <span class="cb-between__sep">e</span>
                        <input
                          type="number"
                          v-model.number="cond.valor[1]"
                          class="cb-input"
                          placeholder="Ate"
                          @input="emitUpdate" />
                      </div>

                      <multiselect
                        v-else-if="getFieldDef(cond.campo).tipo === 'select' && ['em', 'nao_em'].includes(cond.operador)"
                        v-model="cond.valor"
                        :options="getOptionsValuesForField(cond.campo)"
                        :custom-label="(val) => getOptionLabel(cond.campo, val)"
                        :multiple="true"
                        :close-on-select="false"
                        :loading="loadingOptions[cond.campo]"
                        placeholder="Selecione..."
                        @open="loadFieldOptions(cond.campo)"
                        @input="emitUpdate"
                        class="cb-multiselect">
                      </multiselect>

                      <multiselect
                        v-else-if="getFieldDef(cond.campo).tipo === 'select'"
                        v-model="cond.valor"
                        :options="getOptionsValuesForField(cond.campo)"
                        :custom-label="(val) => getOptionLabel(cond.campo, val)"
                        :loading="loadingOptions[cond.campo]"
                        placeholder="Selecione..."
                        @open="loadFieldOptions(cond.campo)"
                        @input="emitUpdate"
                        class="cb-multiselect">
                      </multiselect>

                      <input
                        v-else-if="getFieldDef(cond.campo).tipo === 'date' && cond.operador !== 'entre'"
                        type="date"
                        v-model="cond.valor"
                        class="cb-input"
                        @input="emitUpdate" />

                      <div v-else-if="getFieldDef(cond.campo).tipo === 'date' && cond.operador === 'entre'" class="cb-between">
                        <input
                          type="date"
                          v-model="cond.valor[0]"
                          class="cb-input"
                          @input="emitUpdate" />
                        <span class="cb-between__sep">e</span>
                        <input
                          type="date"
                          v-model="cond.valor[1]"
                          class="cb-input"
                          @input="emitUpdate" />
                      </div>

                      <input
                        v-else
                        type="text"
                        v-model="cond.valor"
                        class="cb-input"
                        placeholder="Valor"
                        @input="emitUpdate" />
                    </template>

                    <!-- Valor relativo (named_param) -->
                    <template v-if="cond.tipo_valor === 'named_param'">
                      <select
                        v-if="cond.operador !== 'entre'"
                        v-model="cond.valor"
                        class="cb-select cb-select--value"
                        @change="emitUpdate">
                        <option value="">Selecione...</option>
                        <option
                          v-for="param in namedDateParams"
                          :key="param.value"
                          :value="param.value">
                          {{ param.label }}
                        </option>
                      </select>
                      <div v-else class="cb-between">
                        <select
                          v-model="cond.valor[0]"
                          class="cb-select cb-select--value"
                          @change="emitUpdate">
                          <option value="">De...</option>
                          <option
                            v-for="param in namedDateParams"
                            :key="param.value"
                            :value="param.value">
                            {{ param.label }}
                          </option>
                        </select>
                        <span class="cb-between__sep">e</span>
                        <select
                          v-model="cond.valor[1]"
                          class="cb-select cb-select--value"
                          @change="emitUpdate">
                          <option value="">Ate...</option>
                          <option
                            v-for="param in namedDateParams"
                            :key="param.value"
                            :value="param.value">
                            {{ param.label }}
                          </option>
                        </select>
                      </div>
                    </template>

                    <!-- Valor dinamico (parametro) -->
                    <template v-if="cond.tipo_valor === 'parametro'">
                      <input
                        type="text"
                        v-model="cond.parametro.label"
                        class="cb-input"
                        placeholder="Label do parametro"
                        @input="emitUpdate" />
                      <span class="cb-badge cb-badge--info" title="Valor solicitado ao usuario no momento do uso">
                        <fa-icon icon="info-circle" size="sm" /> Dinamico
                      </span>
                    </template>
                  </div>
                </div>

                <!-- Indicador operador sem valor -->
                <div v-else-if="isNoValueOperator(cond.operador)" class="cb-row__value">
                  <span class="cb-badge cb-badge--muted">Sem valor</span>
                </div>

                <!-- Remover -->
                <div class="cb-row__remove">
                  <button
                    type="button"
                    class="cb-btn-icon cb-btn-icon--danger"
                    @click="removeCondition(gIndex, cIndex)"
                    :disabled="grupo.condicoes.length <= 1"
                    title="Remover">
                    <fa-icon icon="times" size="sm" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Adicionar condicao -->
            <button
              type="button"
              class="cb-add-condition"
              @click="addCondition(gIndex)">
              <fa-icon icon="plus" size="sm" /> Adicionar condicao
            </button>
          </div>
        </div>

        <!-- Adicionar grupo -->
        <button
          type="button"
          class="cb-add-group"
          @click="addGroup()">
          <fa-icon icon="plus" size="sm" /> Adicionar grupo de condicoes
        </button>

        <!-- Preview SQL -->
        <div class="cb-preview" v-if="sqlPreview">
          <div class="cb-preview__header">
            <span class="cb-preview__title">SQL gerado</span>
            <button
              type="button"
              class="cb-btn-icon"
              @click="refreshPreview"
              title="Atualizar preview">
              <fa-icon icon="sync-alt" size="sm" />
            </button>
          </div>
          <pre class="cb-preview__code">{{ sqlPreview }}</pre>
        </div>
      </div>
    </div>

    <!-- Modo SQL -->
    <div v-else class="cb-sql">
      <codemirror v-model="rawSql" :options="codeOptions" @input="onRawSqlChange"></codemirror>
    </div>
  </div>
</template>

<script>
import ncformCommon from '@ncform/ncform-common';
import { get, post } from '@/helpers/apiRequest';
import { codemirror } from 'vue-codemirror';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/monokai.css';
import 'codemirror/mode/sql/sql.js';
import 'codemirror/addon/display/autorefresh.js';
import 'codemirror/addon/selection/active-line.js';

const OPERATOR_LABELS = {
  igual: 'Igual a',
  diferente: 'Diferente de',
  maior: 'Maior que',
  menor: 'Menor que',
  maior_igual: 'Maior ou igual a',
  menor_igual: 'Menor ou igual a',
  em: 'Esta em',
  nao_em: 'Nao esta em',
  entre: 'Entre',
  contem: 'Contem',
  nulo: 'E nulo',
  nao_nulo: 'Nao e nulo',
};

function newCondition() {
  return {
    campo: '',
    operador: '',
    valor: null,
    tipo_valor: 'static',
    parametro: { nome: '', label: '', tipo: 'text', multiplo: false },
  };
}

function newGroup() {
  return {
    logica: 'AND',
    condicoes: [newCondition()],
  };
}

export default {
  name: 'ConditionBuilder',
  mixins: [ncformCommon.mixins.vue.controlMixin],
  components: { codemirror, Multiselect },

  data() {
    return {
      isVisualMode: true,
      fields: [],
      namedDateParams: [],
      fieldOptionsCache: {},
      loadingOptions: {},
      loadingFields: false,
      conditions: { grupos: [newGroup()] },
      rawSql: '',
      sqlPreview: '',
      previewTimeout: null,
      codeOptions: {
        tabSize: 4,
        styleActiveLine: true,
        lineNumbers: true,
        line: true,
        mode: 'text/x-sql',
        autoRefresh: true,
        lineWrapping: true,
        theme: 'monokai',
        showCursorWhenSelecting: true,
      },
    };
  },

  computed: {
    entidade() {
      return this.formData ? this.formData.entidade : null;
    },
  },

  watch: {
    modelVal: {
      handler(newVal) {
        if (!newVal) return;
        if (typeof newVal === 'string') {
          try {
            const parsed = JSON.parse(newVal);
            if (parsed && parsed.grupos) {
              this.conditions = parsed;
              this.isVisualMode = true;
            } else {
              this.rawSql = newVal;
              this.isVisualMode = false;
            }
          } catch {
            this.rawSql = newVal;
            this.isVisualMode = false;
          }
        } else if (typeof newVal === 'object' && newVal.grupos) {
          this.conditions = newVal;
          this.isVisualMode = true;
        }
      },
      immediate: true,
    },

    entidade: {
      handler(newEntidade) {
        if (newEntidade) {
          this.loadFields(newEntidade);
        }
      },
      immediate: true,
    },

    isVisualMode() {
      this.emitUpdate();
    },
  },

  methods: {
    async loadFields(entidade) {
      this.loadingFields = true;
      try {
        const data = await get(`/admin/tag-filtro/campos/${entidade}`);
        this.fields = data.campos || [];
        this.namedDateParams = data.named_date_params || [];
      } catch (e) {
        console.error('Erro ao carregar campos:', e);
        this.fields = [];
      } finally {
        this.loadingFields = false;
      }
    },

    async loadFieldOptions(campo) {
      if (this.fieldOptionsCache[campo] || this.loadingOptions[campo]) return;

      const fieldDef = this.getFieldDef(campo);
      if (!fieldDef || !fieldDef.opcoes_url) return;

      this.$set(this.loadingOptions, campo, true);
      try {
        const opcoes = await get(`/admin/tag-filtro/opcoes/${this.entidade}/${fieldDef.opcoes_url}`);
        this.$set(this.fieldOptionsCache, campo, opcoes || []);
      } catch (e) {
        console.error('Erro ao carregar opcoes:', e);
        this.$set(this.fieldOptionsCache, campo, []);
      } finally {
        this.$set(this.loadingOptions, campo, false);
      }
    },

    getFieldDef(campo) {
      return this.fields.find(f => f.campo === campo) || { tipo: 'text', operadores: [] };
    },

    getOperatorsForField(campo) {
      const fieldDef = this.getFieldDef(campo);
      return (fieldDef.operadores || []).map(op => ({
        value: op,
        label: OPERATOR_LABELS[op] || op,
      }));
    },

    getOptionsForField(campo) {
      return this.fieldOptionsCache[campo] || [];
    },

    getOptionsValuesForField(campo) {
      return (this.fieldOptionsCache[campo] || []).map(o => o.value);
    },

    getOptionLabel(campo, val) {
      const options = this.fieldOptionsCache[campo] || [];
      const found = options.find(o => o.value === val);
      return found ? found.label : val;
    },

    isNoValueOperator(operador) {
      return ['nulo', 'nao_nulo'].includes(operador);
    },

    onFieldChange(cond) {
      cond.operador = '';
      cond.valor = null;
      cond.tipo_valor = 'static';
      cond.parametro = { nome: '', label: '', tipo: 'text', multiplo: false };

      const fieldDef = this.getFieldDef(cond.campo);
      if (fieldDef.tipo === 'select' && fieldDef.opcoes_url) {
        this.loadFieldOptions(cond.campo);
      }
      this.emitUpdate();
    },

    onOperatorChange(cond) {
      const fieldDef = this.getFieldDef(cond.campo);
      if (cond.operador === 'entre') {
        cond.valor = fieldDef.tipo === 'date' && cond.tipo_valor === 'named_param'
          ? ['', '']
          : [null, null];
      } else if (['em', 'nao_em'].includes(cond.operador)) {
        cond.valor = [];
      } else if (this.isNoValueOperator(cond.operador)) {
        cond.valor = null;
      } else {
        cond.valor = fieldDef.tipo === 'boolean' ? 1 : null;
      }
      this.emitUpdate();
    },

    onTipoValorChange(cond) {
      if (cond.tipo_valor === 'named_param') {
        cond.valor = cond.operador === 'entre' ? ['', ''] : '';
      } else if (cond.tipo_valor === 'parametro') {
        const fieldDef = this.getFieldDef(cond.campo);
        cond.parametro = {
          nome: cond.campo.replace('.', '_').toLowerCase(),
          label: fieldDef.label || cond.campo,
          tipo: fieldDef.tipo || 'text',
          multiplo: ['em', 'nao_em'].includes(cond.operador),
        };
        cond.valor = null;
      } else {
        cond.valor = cond.operador === 'entre' ? [null, null] : null;
      }
      this.emitUpdate();
    },

    addGroup() {
      this.conditions.grupos.push(newGroup());
      this.emitUpdate();
    },

    removeGroup(gIndex) {
      if (this.conditions.grupos.length > 1) {
        this.conditions.grupos.splice(gIndex, 1);
        this.emitUpdate();
      }
    },

    addCondition(gIndex) {
      this.conditions.grupos[gIndex].condicoes.push(newCondition());
      this.emitUpdate();
    },

    removeCondition(gIndex, cIndex) {
      const grupo = this.conditions.grupos[gIndex];
      if (grupo.condicoes.length > 1) {
        grupo.condicoes.splice(cIndex, 1);
        this.emitUpdate();
      }
    },

    emitUpdate() {
      if (this.isVisualMode) {
        this.$emit('input', JSON.stringify(this.conditions));
      } else {
        this.$emit('input', this.rawSql);
      }
      this.debouncePreview();
    },

    onRawSqlChange() {
      this.$emit('input', this.rawSql);
    },

    debouncePreview() {
      if (this.previewTimeout) clearTimeout(this.previewTimeout);
      this.previewTimeout = setTimeout(() => this.refreshPreview(), 800);
    },

    async refreshPreview() {
      if (!this.isVisualMode) {
        this.sqlPreview = '';
        return;
      }

      const hasValidConditions = this.conditions.grupos.some(g =>
        g.condicoes.some(c => c.campo && c.operador)
      );

      if (!hasValidConditions) {
        this.sqlPreview = '';
        return;
      }

      try {
        const result = await post('/admin/tag-filtro/preview-sql', { condicoes: this.conditions });
        this.sqlPreview = result.clausula || '';
      } catch (e) {
        this.sqlPreview = '';
      }
    },
  },

  mounted() {
    if (this.entidade && this.fields.length === 0) {
      this.loadFields(this.entidade);
    }
  },
};
</script>

<style scoped>
/* ============================================================
   BASE
   ============================================================ */
.cb {
  font-size: 13px;
  line-height: 1.5;
  color: #3c4b64;
}

/* ============================================================
   MODE TOGGLE
   ============================================================ */
.cb-mode {
  display: inline-flex;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}
.cb-mode__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  background: #fff;
  color: #768192;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-mode__btn:hover {
  background: #f0f4f8;
  color: #3c4b64;
}
.cb-mode__btn--active {
  background: #3a3e81;
  color: #fff;
}
.cb-mode__btn--active:hover {
  background: #2e3270;
  color: #fff;
}
.cb-mode__btn + .cb-mode__btn {
  border-left: 1px solid #d8dbe0;
}

/* ============================================================
   VISUAL BUILDER
   ============================================================ */
.cb-visual {
  background: #f4f6f9;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  padding: 16px;
}

/* Empty / Loading */
.cb-empty,
.cb-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-size: 13px;
  color: #768192;
  background: #fff;
  border-radius: 6px;
  border: 1px dashed #c4c9d0;
}
.cb-empty__icon {
  color: #39f;
  flex-shrink: 0;
}

/* ============================================================
   GROUP
   ============================================================ */
.cb-group {
  position: relative;
}

/* Conector AND entre grupos */
.cb-connector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
}
.cb-connector__line {
  flex: 1;
  height: 1px;
  background: #c4c9d0;
}
.cb-connector__label {
  font-size: 11px;
  font-weight: 700;
  color: #768192;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 10px;
  background: #ebedef;
  border-radius: 10px;
}

.cb-group__card {
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  overflow: hidden;
}

/* Group Header */
.cb-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8f9fb;
  border-bottom: 1px solid #ebedef;
}
.cb-group__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #3c4b64;
}
.cb-group__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #3a3e81;
  border-radius: 50%;
  flex-shrink: 0;
}
.cb-group__logic {
  appearance: none;
  -webkit-appearance: none;
  padding: 2px 24px 2px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #3a3e81;
  background: #eceaf3 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6'%3E%3Cpath d='M0 0l4 6 4-6z' fill='%233a3e81'/%3E%3C/svg%3E") no-repeat right 8px center;
  border: 1px solid #b8b9d0;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
}
.cb-group__logic:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.15);
}

/* ============================================================
   CONDITIONS
   ============================================================ */
.cb-conditions {
  padding: 12px 14px 4px;
}

.cb-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

/* Prefix (Se / E / OU) */
.cb-row__prefix {
  width: 32px;
  flex-shrink: 0;
  padding-top: 7px;
  text-align: right;
}
.cb-row__prefix-text {
  font-size: 13px;
  font-weight: 600;
  color: #768192;
}
.cb-row__prefix-text--logic {
  color: #3a3e81;
}

/* Columns */
.cb-row__field {
  width: 200px;
  flex-shrink: 0;
}
.cb-row__operator {
  width: 150px;
  flex-shrink: 0;
}
.cb-row__value {
  flex: 1;
  min-width: 0;
}
.cb-row__remove {
  flex-shrink: 0;
  padding-top: 4px;
}

/* ============================================================
   FORM CONTROLS (unified)
   ============================================================ */
.cb-select,
.cb-input {
  display: block;
  width: 100%;
  height: 32px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: #3c4b64;
  background-color: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
}
.cb-select {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 28px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6'%3E%3Cpath d='M0 0l4 6 4-6z' fill='%23768192'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  cursor: pointer;
}
.cb-select:disabled {
  background-color: #ebedef;
  color: #9da5b1;
  cursor: not-allowed;
}
.cb-select:focus,
.cb-input:focus {
  border-color: #9496b8;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.12);
}
.cb-input::placeholder {
  color: #9da5b1;
}

/* Tipo valor (mini select) */
.cb-select--tipo {
  width: 80px;
  flex-shrink: 0;
  font-size: 12px;
  padding: 4px 22px 4px 6px;
  background-position: right 6px center;
  color: #768192;
}

/* Value select (flex) */
.cb-select--value {
  flex: 1;
  min-width: 0;
}

/* Value group */
.cb-value-group {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

/* Between inputs */
.cb-between {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.cb-between__sep {
  font-size: 13px;
  font-weight: 500;
  color: #768192;
  flex-shrink: 0;
}
.cb-between .cb-input,
.cb-between .cb-select {
  flex: 1;
  min-width: 0;
}

/* ============================================================
   BADGES
   ============================================================ */
.cb-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}
.cb-badge--info {
  background: #e8f4fd;
  color: #0d6efd;
}
.cb-badge--muted {
  background: #ebedef;
  color: #768192;
}

/* ============================================================
   ICON BUTTONS
   ============================================================ */
.cb-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: #9da5b1;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-btn-icon:hover {
  color: #3c4b64;
  background: #ebedef;
}
.cb-btn-icon--danger:hover {
  color: #e55353;
  background: #fce4e4;
  border-color: #f5c2c2;
}
.cb-btn-icon:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.cb-btn-icon:disabled:hover {
  background: transparent;
  color: #9da5b1;
}

/* ============================================================
   ADD BUTTONS
   ============================================================ */
.cb-add-condition {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 14px 12px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #3a3e81;
  background: transparent;
  border: 1px dashed #9496b8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-add-condition:hover {
  background: #eceaf3;
  border-color: #3a3e81;
}

.cb-add-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #768192;
  background: #fff;
  border: 1px dashed #c4c9d0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-add-group:hover {
  color: #3a3e81;
  border-color: #9496b8;
  background: #f2f2f8;
}

/* ============================================================
   PREVIEW
   ============================================================ */
.cb-preview {
  margin-top: 14px;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  overflow: hidden;
}
.cb-preview__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: #f8f9fb;
  border-bottom: 1px solid #ebedef;
}
.cb-preview__title {
  font-size: 12px;
  font-weight: 600;
  color: #768192;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.cb-preview__code {
  margin: 0;
  padding: 14px;
  font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #a6e22e;
  background: #1e1e2e;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 180px;
  overflow-y: auto;
}

/* ============================================================
   SQL EDITOR
   ============================================================ */
.cb-sql {
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  overflow: hidden;
}
</style>

<style>
/* ============================================================
   VUE-MULTISELECT OVERRIDES (unscoped for deep styling)
   ============================================================ */
.cb .cb-multiselect.multiselect {
  min-height: 32px;
  font-size: 13px;
}
.cb .cb-multiselect .multiselect__tags {
  min-height: 32px;
  padding: 4px 40px 0 8px;
  font-size: 13px;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
}
.cb .cb-multiselect .multiselect__tags:focus-within {
  border-color: #9496b8;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.12);
}
.cb .cb-multiselect .multiselect__tag {
  margin-bottom: 2px;
  padding: 2px 22px 2px 8px;
  font-size: 12px;
  background: #3a3e81;
  border-radius: 3px;
}
.cb .cb-multiselect .multiselect__tag-icon::after {
  color: rgba(255, 255, 255, 0.7);
}
.cb .cb-multiselect .multiselect__tag-icon:hover {
  background: #2e3270;
}
.cb .cb-multiselect .multiselect__select {
  height: 32px;
}
.cb .cb-multiselect .multiselect__input,
.cb .cb-multiselect .multiselect__single {
  font-size: 13px;
  margin-bottom: 2px;
  padding: 0;
  line-height: 22px;
}
.cb .cb-multiselect .multiselect__placeholder {
  font-size: 13px;
  color: #9da5b1;
  margin-bottom: 2px;
  padding: 0;
  line-height: 22px;
}
.cb .cb-multiselect .multiselect__option {
  font-size: 13px;
  padding: 8px 12px;
  min-height: 32px;
}
.cb .cb-multiselect .multiselect__option--highlight {
  background: #3a3e81;
}
</style>
